import { createAsyncThunk } from "@reduxjs/toolkit";

import { FlightAPI } from "contexts/flight_api_context";
import { FlagNameType, RootState } from "shared/types";
import { ClassEventType } from "shared/types/class_event";
import { LevelType, MonitoringEventType } from "shared/types/monitoring_event";
import { isFlagActiveForClass } from "shared/utils/flag";
import { MONITORING_EVENT_TYPES, getReducedAvState } from "shared/utils/monitoring";

export const createClassEvent = createAsyncThunk(
  "monitoring/createClassEvent",
  async (payload: Omit<ClassEventType, "id" | "timestamp">, { getState }) => {
    const state = getState() as RootState;
    const flightMonitoringUrl = state.local.flightMonitoringUrl;
    if (!flightMonitoringUrl) {
      window.flightLogger?.warn("[createClassEvent] No flightMonitoringUrl found in state");
      return;
    }
    const classId = state.classroom.client.info.id;
    const flightAPI = new FlightAPI(flightMonitoringUrl, payload.userId);
    const response = await flightAPI.postClassEvent(classId, payload);
    if (response && !response.ok) {
      window.flightLogger?.error(
        `[createClassEvent] Failed to create class event (${payload.type}) for user ${payload.userId} in class ${classId}`,
      );
    }
  },
);

export const createMonitoringEvent = createAsyncThunk(
  "monitoring/createMonitoringEvent",
  async (
    payload: Omit<MonitoringEventType, "id" | "timestamp" | "orgId" | "orgShortName" | "classType">,
    { getState },
  ) => {
    const state = getState() as RootState;
    const classId = state.classroom.client.info.id;
    if (isFlagActiveForClass(window.FLAGS, FlagNameType.DISABLE_MONITORING_EVENTS, classId)) {
      window.flightLogger?.warn(
        `Monitoring events are disabled: not creating monitoring event (${payload.type}) for user ${payload.userId}`,
      );
      return;
    }
    if (
      isFlagActiveForClass(
        window.FLAGS,
        FlagNameType.DISABLE_NON_ESSENTIAL_MONITORING_EVENTS,
        classId,
      ) &&
      MONITORING_EVENT_TYPES[payload.type]?.level !== LevelType.IMPORTANT
    ) {
      window.flightLogger?.warn(
        `Non-essential monitoring events are disabled: not creating monitoring event (${payload.type}) for user ${payload.userId}`,
      );
      return;
    }
    const currentAvState = state.av;
    // If we don't have a room_id, A/V has not been initialized and A/V won't contribute to the monitoring event
    const shouldAddAvState =
      state.av && state.av.room_id && MONITORING_EVENT_TYPES[payload.type]?.needsAvState;
    const newEvent = payload as Partial<MonitoringEventType>;
    if (state.classroom?.client.info) {
      newEvent.orgId = `${state.classroom.client.info.org.id}` || "";
      newEvent.orgShortName = state.classroom.client.info.org.short_name;
      newEvent.classType = state.classroom.client.info.type;
    }
    if (shouldAddAvState && currentAvState) {
      const avState = getReducedAvState(currentAvState);
      // Create a new monitoring event with the AV state if there is A/V
      newEvent.contextData = { ...newEvent.contextData, avState };
    }
    const flightMonitoringUrl = state.local.flightMonitoringUrl;
    if (!flightMonitoringUrl) {
      window.flightLogger?.warn("[createMonitoringEvent] No flightMonitoringUrl found in state");
      return;
    }
    const flightAPI = new FlightAPI(flightMonitoringUrl, payload.userId);
    const response = await flightAPI.postMonitoringEvent(classId, newEvent);
    if (response && !response.ok) {
      window.flightLogger?.error(
        `[createMonitoringEvent] Failed to create monitoring event (${newEvent.type}) for user ${newEvent.userId} in class ${classId}`,
      );
    }
  },
);
